import { useState } from 'react';
import { useIntl } from 'react-intl';
import MfaEnrollmentGateShell from './MfaEnrollmentGateShell';
import { useMfaStepperUi } from './mfa/MfaStepper';

/**
 * RD-SEC-008 / RD-SEC-015 — Mandatory MFA enrollment interstitial for tenant
 * admins. Tenant admins manage company + customer data, so TOTP is REQUIRED,
 * not optional. ProtectedRoute renders this full-screen gate (instead of the
 * app) whenever a valid, active tenant admin has no verified TOTP factor.
 *
 * RD-SEC-015: `useMfaStepperUi` (`mode="gate"`) owns the whole enrollment
 * flow — steps, resume detection, iPhone handoff, recovery codes — and calls
 * `onEnrollmentComplete`/`onRecoveryHoldChange` itself per the LOCKED
 * hold/complete contract: the hold arms the instant verify succeeds and is
 * held through the recovery-codes + done steps; the gate clears ONLY via
 * `onEnrollmentComplete` (wired to "Ir al inicio"). A recovery-codes-issue
 * failure never traps the user — the hook calls `onEnrollmentComplete`
 * immediately in that case.
 *
 * RD-SEC-015 (2026-09-25 anatomy fix): this gate calls the hook directly
 * (rather than rendering `<MfaStepper>`) so its `header`/`footer` land in
 * `MfaEnrollmentGateShell`'s pinned regions (via `LoginAccessCard`) instead
 * of inside the scrollable body — see that shell's doc comment.
 *
 * PAIRED PATTERN: this gate and `PlatformMfaEnrollmentGate` are a deliberate
 * mirror — both are thin wrappers around `MfaEnrollmentGateShell` +
 * `useMfaStepperUi`. Keep both in lockstep; a change here must land there too.
 */
export default function TenantMfaEnrollmentGate({ onEnrolled, onSignOut, onRecoveryHoldChange }) {
  const intl = useIntl();
  const [footerHidden, setFooterHidden] = useState(false);

  const { header, body, footer } = useMfaStepperUi({
    mode: 'gate',
    onEnrollmentComplete: onEnrolled,
    onRecoveryHoldChange,
    onFooterHiddenChange: setFooterHidden,
  });

  return (
    <MfaEnrollmentGateShell
      ariaLabel={intl.formatMessage({ id: 'mfaStepper.name', defaultMessage: 'Two-step verification' })}
      stepHeader={header}
      footer={footer}
      onSignOut={onSignOut}
      hideFooter={footerHidden}
      signOutLabel={intl.formatMessage({
        id: 'mfaStepper.signOut',
        defaultMessage: 'Wrong account? Sign out',
      })}
    >
      {body}
    </MfaEnrollmentGateShell>
  );
}
